import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  signInWithPopup,
  signInWithCustomToken,
  signInWithCredential,
  signInWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  FacebookAuthProvider,
} from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import { C } from "../theme";
import Button from "../components/Button";
import SponsorBand from "../components/SponsorBand";
import { auth, googleProvider, facebookProvider, db } from "../firebase";
import { APP_VERSION } from "../appVersion";
import { resolveApiBaseUrl, resolveHostedAppBaseUrl } from "../services/apiBase";
import { getCurrentSessionAttribution } from "../utils/promo";
import { fetchLoginSponsors, readCachedLoginSponsors } from "../services/loginSponsors";

// Server-minted auth emails (sign-in link, password reset, verify) go
// through our Cloud Function so the message is delivered by our SendGrid
// extension, not Firebase Auth's outbound SMTP (which was being blocked).
async function requestAuthEmail({ action, email, continueUrl }) {
  const base = resolveApiBaseUrl() || "";
  const url = `${base}/v1/auth/email`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action,
      email,
      continueUrl,
      publicBaseUrl: continueUrl,
    }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const payload = await res.json();
      detail = payload?.error?.message || "";
    } catch (_) {}
    const err = new Error(detail || `Email request failed (${res.status}).`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

const EULA_VERSION = "2026-04-16";

export default function AuthScreen() {
  const [mode, setMode] = useState("signin");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [oauthFlowStage, setOauthFlowStage] = useState("");
  const [oauthFlowProvider, setOauthFlowProvider] = useState("");
  const [showLegalLinks, setShowLegalLinks] = useState(false);
  const [legalView, setLegalView] = useState(null);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const hasAutoBookAttribution = useMemo(() => {
    const attr = getCurrentSessionAttribution();
    return attr?.source === "book";
  }, []);
  const [pendingEmailSent, setPendingEmailSent] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [signupSwitchBanner, setSignupSwitchBanner] = useState("");
  const [introVideoPlaying, setIntroVideoPlaying] = useState(false);
  const [introVideoExpanded, setIntroVideoExpanded] = useState(false);
  const [introVideoPaused, setIntroVideoPaused] = useState(false);
  const [introSeen] = useState(() => {
    try { return localStorage.getItem("the99.introSeen") === "1"; } catch { return false; }
  });
  const introVideoRef = React.useRef(null);
  const INTRO_VIDEO_SRC = "/intro.mp4";
  const INTRO_VIDEO_POSTER = "/intro-poster.jpg";
  const authSurfaceText = "#F5FAFF";
  const authSurfaceMuted = "rgba(224,236,247,0.86)";

  // Rotating login sponsors (public, display-only). Seed from cache for an
  // instant/offline first paint, then refresh from Firestore.
  const [loginSponsors, setLoginSponsors] = useState(() => readCachedLoginSponsors());
  useEffect(() => {
    let cancelled = false;
    fetchLoginSponsors()
      .then((list) => { if (!cancelled) setLoginSponsors(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const getProviderLabel = (providerId = "") => {
    if (providerId === "facebook.com") return "Facebook";
    if (providerId === "google.com") return "Google";
    if (providerId === "password") return "Email";
    if (providerId === "emailLink") return "Email activation";
    return "your provider";
  };

  const oauthProviderTheme = useMemo(() => {
    if (oauthFlowProvider === "facebook.com") {
      return {
        accent: "#1877F2",
        accentSoft: "rgba(24,119,242,0.2)",
        iconBg: "linear-gradient(145deg,#1877F2,#0F5FC8)",
        panelBorder: "rgba(24,119,242,0.45)",
        panelGlow: "0 20px 44px rgba(10,40,84,0.55)",
      };
    }
    if (oauthFlowProvider === "google.com") {
      return {
        accent: "#34A853",
        accentSoft: "rgba(52,168,83,0.2)",
        iconBg: "linear-gradient(145deg,#34A853,#22793A)",
        panelBorder: "rgba(52,168,83,0.45)",
        panelGlow: "0 20px 44px rgba(15,60,26,0.55)",
      };
    }
    return {
      accent: C.seafoam,
      accentSoft: "rgba(37,99,235,0.22)",
      iconBg: "linear-gradient(145deg,#2563EB,#1746A2)",
      panelBorder: `${C.border}`,
      panelGlow: "0 20px 44px rgba(0,0,0,0.35)",
    };
  }, [oauthFlowProvider]);

  const isInAppBrowser = () => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /FBAN|FBAV|FB_IAB|Instagram|Line\/|LinkedInApp|Twitter|Snapchat|GSA|; wv\)|\bwv\b|Outlook|Teams|MSOffice|OfficeHub|WebView/i.test(ua);
  };

  const isCapacitorNative = () => {
    try {
      if (Capacitor?.isNativePlatform?.()) return true;
      if (Capacitor?.getPlatform && Capacitor.getPlatform() !== "web") return true;
    } catch {}
    if (typeof window === "undefined") return false;
    return Boolean(
      window.Capacitor?.isNativePlatform?.() ||
        window.Capacitor?.platform === "android" ||
        window.Capacitor?.platform === "ios",
    );
  };

  const modeLabel = useMemo(() => (mode === "signin" ? "Sign In" : "Create Account"), [mode]);

  const splitDisplayName = (displayName = "") => {
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return { firstName: "", lastName: "" };
    return {
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" "),
    };
  };

  const formatPhoneForDisplay = (value = "") => {
    const digits = String(value).replace(/\D/g, "").slice(0, 10);
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const line = digits.slice(6, 10);

    if (!digits) return "";
    if (digits.length <= 3) return `(${area}`;
    if (digits.length <= 6) return `(${area}) ${prefix}`;
    return `(${area}) ${prefix}-${line}`;
  };

  const upsertUserProfile = useCallback(async (user, overrides = {}) => {
    if (!user?.uid) return;

    const derivedName = splitDisplayName(user.displayName || "");
    const resolvedFirstName = (overrides.firstName ?? derivedName.firstName).trim();
    const resolvedLastName = (overrides.lastName ?? derivedName.lastName).trim();
    const trimmedEmail = (overrides.email ?? user.email ?? "").trim();
    const trimmedPhone = (overrides.phone ?? "").trim();
    const loginProvider = String(overrides.loginProvider || "").trim();
    const hasEulaAcceptance = overrides.eulaAccepted === true;

    const payload = {
      uid: user.uid,
      email: trimmedEmail,
      loginTime: serverTimestamp(),
    };

    if (resolvedFirstName) payload.firstName = resolvedFirstName;
    if (resolvedLastName) payload.lastName = resolvedLastName;
    if (trimmedPhone) payload.phone = trimmedPhone;
    if (loginProvider) payload.loginProvider = loginProvider;
    if (hasEulaAcceptance) {
      payload.eula = {
        accepted: true,
        version: String(overrides.eulaVersion || EULA_VERSION),
        acceptedAt: serverTimestamp(),
        acceptedVia: String(overrides.eulaAcceptedVia || "auth"),
      };
    }

    try {
      await setDoc(doc(db, "users", user.uid), payload, { merge: true });
    } catch (error) {
      if (error?.code === "permission-denied") {
        console.warn("[Auth] profile upsert skipped due to Firestore permissions:", error);
        return;
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const hash = window.location.hash || "";
    if (!hash.startsWith("#")) return undefined;

    const fragment = new URLSearchParams(hash.slice(1));
    const customToken = String(fragment.get("facebookAuthToken") || "").trim();
    const callbackError = String(fragment.get("facebookAuthError") || "").trim();

    if (!customToken && !callbackError) {
      return undefined;
    }

    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, cleanUrl);

    if (callbackError) {
      setOauthFlowProvider("");
      setOauthFlowStage("");
      setLoading(false);
      setError(callbackError);
      return undefined;
    }

    let cancelled = false;

    const completeFacebookSignIn = async () => {
      setError("");
      setLoading(true);
      setOauthFlowProvider("facebook.com");
      setOauthFlowStage("returning");
      try {
        const result = await signInWithCustomToken(auth, customToken);
        try {
          localStorage.setItem("the99.currentLoginProvider", "facebook.com");
        } catch {}
        let pendingEula = null;
        try {
          const raw = localStorage.getItem(FB_PENDING_EULA_KEY);
          if (raw) pendingEula = JSON.parse(raw);
        } catch {}
        await upsertUserProfile(result.user, {
          loginProvider: "facebook.com",
          eulaAccepted: pendingEula?.accepted === true,
          eulaVersion: pendingEula?.version || EULA_VERSION,
          eulaAcceptedVia: "oauth:facebook.com",
        });
        try { localStorage.removeItem(FB_PENDING_EULA_KEY); } catch {}
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Facebook sign-in could not be completed. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setOauthFlowProvider("");
          setOauthFlowStage("");
        }
      }
    };

    completeFacebookSignIn();

    return () => {
      cancelled = true;
    };
  }, [upsertUserProfile]);

  const beginFacebookRedirectSignIn = useCallback(async () => {
    const baseUrl = resolveApiBaseUrl();
    const hostedFallbackBaseUrl = resolveHostedAppBaseUrl();
    const returnTo = `${window.location.origin}${window.location.pathname}`;
    const candidateBaseUrls = [baseUrl, hostedFallbackBaseUrl].filter(Boolean).filter((value, index, arr) => arr.indexOf(value) === index);

    let response = null;

    for (const candidateBaseUrl of candidateBaseUrls) {
      try {
        response = await fetch(
          `${candidateBaseUrl}/v1/auth/facebook/start?returnTo=${encodeURIComponent(returnTo)}`,
          {
            method: "GET",
          }
        );

        if (response.ok) {
          break;
        }
      } catch (error) {
        void error;
      }
    }

    if (!response) {
      throw new Error(
        "Facebook sign-in could not reach the login service. Refresh the app and try again. If this continues, the hosting rewrite or function endpoint may be unavailable."
      );
    }

    const text = await response.text();
    let payload = {};
    try {
      payload = text ? JSON.parse(text) : {};
    } catch {
      payload = {};
    }

    if (!response.ok) {
      throw new Error(
        payload?.error?.message || payload?.message || `Facebook sign-in could not start (${response.status}).`
      );
    }

    const authorizationUrl = String(payload?.authorizationUrl || "").trim();
    if (!authorizationUrl) {
      throw new Error("Facebook sign-in could not start. Missing authorization URL.");
    }

    setOauthFlowStage("redirecting");
    window.location.assign(authorizationUrl);
  }, []);

  // EULA acceptance is captured in component state, which is wiped when we
  // redirect to Facebook. Stash it so the return-from-FB handler can write it
  // to the user profile alongside the new Firebase user.
  const FB_PENDING_EULA_KEY = "the99.pendingFacebookEula";

  const handleProviderSignIn = async (provider) => {
    setError("");
    setSignupSwitchBanner("");
    setLoading(true);
    const providerId = provider?.providerId || "";
    let isLeavingForFacebook = false;
    setOauthFlowProvider(providerId);
    setOauthFlowStage("opening");
    // Clicking an OAuth button in signup mode counts as accepting the EULA;
    // the disclaimer next to the buttons makes that explicit so we don't
    // need a separate checkbox for the social flow.
    const oauthEulaAccepted = mode === "signup" ? true : false;
    try {
      if (providerId === "facebook.com" && isInAppBrowser() && !isCapacitorNative()) {
        setOauthFlowStage("");
        setOauthFlowProvider("");
        setError("Facebook login is blocked in this in-app browser. Open THE 99 in Chrome/Safari and try again.");
        return;
      }

      if (providerId === "facebook.com" && !isCapacitorNative()) {
        if (mode === "signup") {
          try {
            localStorage.setItem(
              FB_PENDING_EULA_KEY,
              JSON.stringify({ accepted: true, version: EULA_VERSION }),
            );
          } catch {}
        }
        await beginFacebookRedirectSignIn();
        isLeavingForFacebook = true;
        return;
      }

      // Google sign-in: in Capacitor (native) we must use the native plugin —
      // signInWithPopup can't open popups in the WebView and falls back to a
      // redirect that strands the user in an external browser. On web we keep
      // the popup flow.
      let signedInUser;
      if (providerId === "google.com" && isCapacitorNative()) {
        const { FirebaseAuthentication } = await import(
          "@capacitor-firebase/authentication"
        );
        const native = await FirebaseAuthentication.signInWithGoogle();
        const idToken = native?.credential?.idToken;
        if (!idToken) {
          throw new Error("Google sign-in did not return an ID token.");
        }
        const cred = GoogleAuthProvider.credential(idToken);
        const userCred = await signInWithCredential(auth, cred);
        signedInUser = userCred.user;
      } else if (providerId === "facebook.com" && isCapacitorNative()) {
        const { FirebaseAuthentication } = await import(
          "@capacitor-firebase/authentication"
        );
        // Clear any cached Facebook session before opening the dialog.
        // Without this, a stale token from a previous attempt (e.g. an
        // account-conflict bail-out) can cause signInWithFacebook to hang
        // and strand the user on the "Connecting to Facebook" overlay.
        try { await FirebaseAuthentication.signOut(); } catch {}
        const native = await FirebaseAuthentication.signInWithFacebook();
        const accessToken = native?.credential?.accessToken;
        if (!accessToken) {
          throw new Error("Facebook sign-in did not return an access token.");
        }
        const fbEmail = native?.user?.email || "";
        const cred = FacebookAuthProvider.credential(accessToken);
        try {
          const userCred = await signInWithCredential(auth, cred);
          signedInUser = userCred.user;
        } catch (credErr) {
          if (credErr && fbEmail && !credErr.customData) {
            credErr.customData = { email: fbEmail };
          }
          // Drop the cached native FB session so the original-provider
          // sign-in (Google/email) isn't blocked and a future Facebook
          // retry starts clean.
          try { await FirebaseAuthentication.signOut(); } catch {}
          throw credErr;
        }
      } else {
        const result = await signInWithPopup(auth, provider);
        signedInUser = result.user;
      }
      setOauthFlowStage("returning");
      try {
        localStorage.setItem("the99.currentLoginProvider", providerId);
      } catch {}
      await upsertUserProfile(signedInUser, {
        loginProvider: providerId,
        eulaAccepted: oauthEulaAccepted,
        eulaVersion: EULA_VERSION,
        eulaAcceptedVia: `oauth:${providerId || "unknown"}`,
      });
    } catch (err) {
      console.error("[Auth] OAuth sign-in error", {
        provider: provider?.providerId,
        code: err?.code,
        message: err?.message,
        native: isCapacitorNative(),
        err,
      });
      if (err?.code === "auth/popup-blocked") {
        setOauthFlowStage("");
        setOauthFlowProvider("");
        setError(
          "Popup blocked. Please allow popups for this site and try again."
        );
        return;
      }
      if (provider?.providerId === "facebook.com" && isInAppBrowser() && !isCapacitorNative()) {
        setOauthFlowStage("");
        setOauthFlowProvider("");
        setError("Facebook login is blocked in this in-app browser. Open THE 99 in Chrome/Safari and try again.");
        return;
      }
      if (err?.code === "auth/popup-closed-by-user") {
        // User dismissed the popup — reset silently
        setOauthFlowStage("");
        setOauthFlowProvider("");
        return;
      }
      if (err?.code === "auth/account-exists-with-different-credential") {
        const conflictEmail = String(err?.customData?.email || "").trim();
        if (conflictEmail) {
          const methods = await fetchSignInMethodsForEmail(auth, conflictEmail).catch(() => []);
          if (methods.includes("google.com")) {
            setError(
              `An account already exists for ${conflictEmail} with Google. Sign in with Google first, then use Facebook on that same account.`
            );
          } else if (methods.includes("password")) {
            setError(
              `An account already exists for ${conflictEmail} with email/password. Sign in with email first, then use Facebook on that same account.`
            );
          } else {
            setError(
              `An account already exists for ${conflictEmail} with a different sign-in method. Use that method first, then connect Facebook.`
            );
          }
        } else {
          setError("An account already exists with a different sign-in method. Use your original sign-in provider first.");
        }
      } else {
        setOauthFlowStage("");
        setOauthFlowProvider("");
        setError(err?.message || "Login failed. Please try again.");
      }
    } finally {
      if (!isLeavingForFacebook) {
        setLoading(false);
        setOauthFlowStage("");
        setOauthFlowProvider("");
      }
    }
  };

  const handleForgotPassword = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError("Enter your email address above, then tap Forgot password.");
      return;
    }
    setError("");
    setSignupSwitchBanner("");
    setLoading(true);
    try {
      // continueUrl must be a public https origin (not capacitor://localhost
      // from the native webview) so the email logo + CTA links resolve.
      const hostedBase = resolveHostedAppBaseUrl() || window.location.origin;
      await requestAuthEmail({
        action: "password_reset",
        email: normalizedEmail,
        continueUrl: hostedBase,
      });
      setPendingEmail(normalizedEmail);
      setResetEmailSent(true);
    } catch (err) {
      console.warn("[Auth] password reset failed:", {
        code: err?.code,
        message: err?.message,
        email: normalizedEmail,
      });
      if (err?.code === "auth/invalid-email") {
        setError("That email address doesn't look right.");
      } else if (err?.code === "auth/too-many-requests") {
        setError("Too many attempts. Please wait a minute and try again.");
      } else if (err?.code === "auth/missing-email") {
        setError("Enter your email address above, then tap Forgot password.");
      } else {
        setError(err?.message || "Could not send reset email. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSignupSwitchBanner("");
    const phoneDigits = phone.replace(/\D/g, "");
      if (!email) {
        setError("Email address is required.");
      return;
    }
      if (mode === "signin" && !password) {
        setError("Password is required.");
        return;
      }
    if (mode === "signup" && (!firstName.trim() || !lastName.trim())) {
      setError("First and last name are required.");
      return;
    }
    if (mode === "signup" && !phone.trim()) {
      setError("Mobile phone number is required.");
      return;
    }
    if (mode === "signup") {
      if (phoneDigits.length !== 10) {
        setError("Enter a valid 10-digit US mobile number.");
        return;
      }
      if (!eulaAccepted) {
        setError("You must accept the EULA to create an account.");
        return;
      }
    }

    const normalizedEmail = email.trim().toLowerCase();

    setLoading(true);
    try {
      if (mode === "signin") {
        const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail).catch(() => null);

        // With Firebase email-enumeration protection enabled, this can be empty even for real accounts.
        // Only block password sign-in when we positively identify a non-password provider.
        if (Array.isArray(methods) && methods.length > 0 && !methods.includes("password")) {
          if (methods.includes("emailLink")) {
            setError("This account exists, but email activation has not been completed yet. Open the activation link from your email, or create the account again to send a new activation email.");
            return;
          }

          const providerLabel = methods.includes("google.com")
            ? "Google"
            : methods.includes("facebook.com")
            ? "Facebook"
            : "your original sign-in provider";
          setError(`This email is set up with ${providerLabel}. Please sign in using that option.`);
          return;
        }

        const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);
        try {
          localStorage.setItem("the99.currentLoginProvider", "password");
        } catch {}
        await upsertUserProfile(result.user, { email: normalizedEmail, loginProvider: "password" });
      } else {
        // Store pending signup data so ActivationScreen can complete profile creation
        try {
          localStorage.setItem(
            "the99.pendingSignup",
            JSON.stringify({
              email: normalizedEmail,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              phone: phoneDigits,
              password: password || "",
              eulaAccepted: true,
            })
          );
          localStorage.setItem("the99.emailForSignIn", normalizedEmail);
        } catch {}

        const hostedBase = resolveHostedAppBaseUrl() || window.location.origin;
        const actionCodeSettings = {
          url: hostedBase,
          handleCodeInApp: true,
        };
        await requestAuthEmail({
          action: "signin_link",
          email: normalizedEmail,
          continueUrl: actionCodeSettings.url,
        });
        setPendingEmail(normalizedEmail);
        setPendingEmailSent(true);
      }
    } catch (err) {
      console.warn("[Auth] email sign-in failed:", {
        code: err?.code,
        message: err?.message,
        email: normalizedEmail,
      });
      if (err?.code === "auth/operation-not-allowed") {
        if (mode === "signup") {
          setError("New account email activation is disabled in Firebase. Enable Authentication > Sign-in method > Email/Password and turn on Email link (passwordless sign-in), then try again.");
        } else {
          setError("Email/password sign-in is disabled in Firebase. Enable Authentication > Sign-in method > Email/Password, then try again.");
        }
      } else if (err?.code === "auth/user-not-found") {
        setMode("signup");
        setPassword("");
        setSignupSwitchBanner("No account found, switched to Create Account.");
      } else if (err?.code === "auth/wrong-password" || err?.code === "auth/invalid-credential") {
        const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail).catch(() => null);
        console.warn("[Auth] sign-in methods for email:", normalizedEmail, methods);

        if (Array.isArray(methods) && methods.includes("emailLink") && !methods.includes("password")) {
          setError("This account exists, but email activation has not been completed yet. Open the activation link from your email, or create the account again to send a new activation email.");
        } else if (Array.isArray(methods) && methods.length > 0 && !methods.includes("password")) {
          const providerId = methods.includes("google.com")
            ? "google.com"
            : methods.includes("facebook.com")
            ? "facebook.com"
            : methods[0];
          setError(`This email is set up with ${getProviderLabel(providerId)}. Please sign in using that option.`);
        } else {
          setError("Incorrect email or password. Please try again.");
        }
      } else if (err?.code === "auth/too-many-requests") {
        setError("Too many attempts. Please wait a minute and try again.");
      } else {
        setError(err?.message || "Email auth failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (resetEmailSent) {
    return (
      <div style={{ flex: 1, position: "relative", overflow: "hidden", background: C.appBackdrop }}>
        <div
          className="hide-scrollbar"
          style={{
            position: "absolute",
            inset: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "32px 20px 40px",
            gap: 10,
          }}
        >
          <div style={{ marginBottom: 4 }}>
            <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 2 }}>THE 99</div>
            <div style={{ fontSize: 22, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>
              Check Your Email
            </div>
          </div>

          <div
            style={{
              background: C.cardBg,
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              borderRadius: 16,
              border: `1px solid ${C.border}`,
              padding: "18px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 999,
                  background: `${C.seafoam}26`,
                  border: `1px solid ${C.seafoam}55`,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M20 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" fill={C.seafoam}/>
                </svg>
              </div>
            </div>

            <div style={{ fontSize: 13, color: C.white, fontWeight: 700, textAlign: "center" }}>
              Password reset link sent
            </div>

            <div style={{ fontSize: 11, color: C.white, opacity: 0.86, lineHeight: 1.6, textAlign: "center" }}>
              If an account exists for{" "}
              <span style={{ color: C.seafoam, fontWeight: 700 }}>{pendingEmail}</span>,
              we just emailed a password reset link. Open the email and follow the link
              to set a new password, then return here to sign in.
            </div>

            <div
              style={{
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                background: C.deepWater,
                padding: "10px 12px",
                fontSize: 11,
                color: C.muted,
                lineHeight: 1.55,
              }}
            >
              <div style={{ color: C.white, fontWeight: 700, marginBottom: 4 }}>What happens next</div>
              <div>1. Open the email from THE 99.</div>
              <div>2. Click the <span style={{ color: C.seafoam }}>Reset password</span> link.</div>
              <div>3. Enter a new password on the next screen.</div>
              <div>4. Return here and sign in with your new password.</div>
            </div>

            <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.5, textAlign: "center" }}>
              Don't see it? Check your spam folder. Reset links expire after about an hour.
            </div>

            {error && (
              <div style={{ fontSize: 11, color: C.warning, lineHeight: 1.4 }}>{error}</div>
            )}

            <button
              type="button"
              onClick={() => {
                setResetEmailSent(false);
                setError("");
              }}
              style={{
                marginTop: 4,
                background: "transparent",
                border: "none",
                color: C.seafoam,
                cursor: "pointer",
                fontSize: 11,
              }}
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (pendingEmailSent) {
    return (
      <div style={{ flex: 1, position: "relative", overflow: "hidden", background: C.appBackdrop }}>
        <div
          className="hide-scrollbar"
          style={{
            position: "absolute",
            inset: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "32px 20px 40px",
            gap: 10,
          }}
        >
          <div style={{ marginBottom: 4 }}>
            <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 2 }}>THE 99</div>
            <div style={{ fontSize: 22, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>
              Check Your Email
            </div>
          </div>

          <div
            style={{
              background: C.cardBg,
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              borderRadius: 16,
              border: `1px solid ${C.border}`,
              padding: "18px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 999,
                  background: `${C.seafoam}26`,
                  border: `1px solid ${C.seafoam}55`,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M20 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" fill={C.seafoam}/>
                </svg>
              </div>
            </div>

            <div style={{ fontSize: 13, color: C.white, fontWeight: 700, textAlign: "center" }}>
              Activation link sent
            </div>

            <div style={{ fontSize: 11, color: C.white, opacity: 0.86, lineHeight: 1.6, textAlign: "center" }}>
              We sent an activation link to{" "}
              <span style={{ color: C.seafoam, fontWeight: 700 }}>{pendingEmail}</span>.
              Click the link in that email to create your password and activate your account.
            </div>

            <div
              style={{
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                background: C.deepWater,
                padding: "10px 12px",
                fontSize: 11,
                color: C.muted,
                lineHeight: 1.55,
              }}
            >
              <div style={{ color: C.white, fontWeight: 700, marginBottom: 4 }}>What happens next</div>
              <div>1. Open the email from THE 99.</div>
              <div>2. Click the <span style={{ color: C.seafoam }}>Activate account</span> link.</div>
              <div>3. Create your password on the next screen.</div>
              <div>4. Sign in and start using THE 99.</div>
            </div>

            {error && (
              <div style={{ fontSize: 11, color: C.warning, lineHeight: 1.4 }}>{error}</div>
            )}

            <button
              type="button"
              onClick={() => {
                setPendingEmailSent(false);
                setError("");
              }}
              style={{
                marginTop: 4,
                background: "transparent",
                border: "none",
                color: C.seafoam,
                cursor: "pointer",
                fontSize: 11,
              }}
            >
              Didn't receive it? Go back and try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (legalView) {
    const isPrivacy = legalView === "privacy";
    const isDeletion = legalView === "deletion";
    return (
      <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "#04060a" }}>
        <div style={{ position: "absolute", inset: 0, background: "rgba(2,6,12,0.35)" }} />
        <div
          className="hide-scrollbar"
          style={{
            position: "absolute",
            inset: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            padding: "14px 16px 24px",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div>
              <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 2 }}>THE 99</div>
              <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>
                {isPrivacy ? "Privacy Policy" : isDeletion ? "Data Deletion" : "End User License Agreement"}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setLegalView(null)}>
              Back
            </Button>
          </div>

          <div
            style={{
              borderRadius: 16,
              border: `1px solid ${C.border}`,
              background: "rgba(4,8,15,0.82)",
              padding: "14px 12px",
              color: C.muted,
              fontSize: 11,
              lineHeight: 1.6,
            }}
          >
            {isPrivacy ? (
              <>
                <div style={{ color: C.white, marginBottom: 8 }}>
                  THE 99 collects account, tournament, session, location, and optional video data to provide app
                  features.
                </div>
                <div>• Account: name, email, login provider, timestamps, and role metadata.</div>
                <div>• Tournament: team and team-follow preferences.</div>
                <div>• Session: catches, timestamps, and conditions.</div>
                <div>• Media: uploaded catch videos and certification metadata.</div>
                <div style={{ marginTop: 10 }}>
                  We use Firebase services for authentication and data storage. We do not sell your personal data.
                </div>
                <div style={{ marginTop: 10 }}>
                  You may request deletion of your account data using the Data Deletion view.
                </div>
              </>
            ) : isDeletion ? (
              <>
                <div style={{ color: C.white, marginBottom: 8 }}>
                  To request deletion of your THE 99 account data:
                </div>
                <div>1. Sign in to your account.</div>
                <div>2. Sign out from app settings (gear icon) if needed.</div>
                <div>3. Email support with subject “Data Deletion Request”.</div>
                <div style={{ marginTop: 10 }}>
                  Contact: <span style={{ color: C.seafoam }}>support@the99.app</span>
                </div>
                <div style={{ marginTop: 10 }}>
                  Include your account email and any identifying details needed to verify ownership.
                </div>
              </>
            ) : (
              <>
                <div style={{ color: C.white, marginBottom: 8 }}>
                  End User License Agreement (Version {EULA_VERSION})
                </div>
                <div>• Legal entity: One Eleven Publishing LLC, Florida, USA.</div>
                <div>• You receive a revocable, non-transferable license to use THE 99.</div>
                <div>• You must follow all tournament, boating, and fishing laws.</div>
                <div>• AI insights are informational and may be inaccurate.</div>
                <div>• You retain your content, and grant service-use rights required to operate THE 99.</div>
                <div>• The service is provided "as is" with standard warranty and liability limits.</div>
                <div style={{ marginTop: 10 }}>
                  Governing law: Florida, USA. Full legal text is available at <span style={{ color: C.seafoam }}>/eula.html</span>.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "#04060a" }}>

      {/* Full-bleed backdrop — Before the Cast cover art, fixed behind content */}
      <iframe
        src="/assets/before_the_cast_cover.html"
        title="backdrop"
        scrolling="no"
        style={{
          position: "absolute",
          width: 420,
          height: 630,
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%) scale(0.92)",
          transformOrigin: "bottom center",
          border: "none",
          pointerEvents: "none",
        }}
      />

      {/* Dark overlay for readability */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(2,6,12,0.25)", pointerEvents: "none" }} />

      {/* Scrollable content layer — sits above backdrop */}
      <div
        className="hide-scrollbar"
        style={{
          position: "absolute",
          inset: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          padding: "14px 18px 24px",
          gap: 10,
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 2 }}>THE 99</div>
          <div style={{ fontSize: 22, color: authSurfaceText, fontFamily: "Georgia,serif", fontWeight: 700 }}>Welcome</div>
          <div style={{ fontSize: 11, color: authSurfaceMuted, marginTop: 2 }}>Sign in with Google, Facebook, or Email</div>
        </div>

        {/* Intro video — tap-to-play, self-hosted MP4 (drop file at public/intro.mp4). Plays with sound after user gesture. Hidden after first successful auth. */}
        {!introSeen && (
        <div
          style={
            introVideoExpanded
              ? {
                  position: "fixed",
                  inset: 0,
                  zIndex: 9999,
                  background: "rgba(0,0,0,0.95)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 16,
                }
              : {
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%",
                  borderRadius: 12,
                  overflow: "hidden",
                  border: `1px solid ${C.border}`,
                  background: "#000",
                  marginBottom: 4,
                }
          }
        >
          <div
            style={
              introVideoExpanded
                ? {
                    position: "relative",
                    width: "100%",
                    maxWidth: 960,
                    aspectRatio: "16 / 9",
                    background: "#000",
                    borderRadius: 12,
                    overflow: "hidden",
                    border: `1px solid ${C.border}`,
                  }
                : { position: "absolute", inset: 0 }
            }
          >
            {introVideoPlaying ? (
              <video
                ref={introVideoRef}
                src={INTRO_VIDEO_SRC}
                poster={INTRO_VIDEO_POSTER}
                autoPlay
                playsInline
                preload="auto"
                onPlay={() => setIntroVideoPaused(false)}
                onPause={() => setIntroVideoPaused(true)}
                onEnded={() => {
                  setIntroVideoPlaying(false);
                  setIntroVideoExpanded(false);
                  setIntroVideoPaused(false);
                }}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
              />
            ) : (
              <button
                type="button"
                onClick={() => setIntroVideoPlaying(true)}
                aria-label="Play intro video"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  padding: 0,
                  border: 0,
                  cursor: "pointer",
                  background: "radial-gradient(ellipse at center, #0e2a3d 0%, #061320 70%, #02080f 100%)",
                  overflow: "hidden",
                }}
              >
                <img
                  src={INTRO_VIDEO_POSTER}
                  alt=""
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: 0.35 }}
                />
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `radial-gradient(circle at 50% 45%, ${C.teal}33 0%, transparent 55%)`,
                  }}
                />
                <img
                  src="/the99-logo.png"
                  alt="the99"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, calc(-50% - 14px))",
                    width: "46%",
                    maxWidth: 220,
                    height: "auto",
                    filter: "drop-shadow(0 4px 18px rgba(0,0,0,0.85)) drop-shadow(0 0 24px rgba(56,178,172,0.35))",
                    pointerEvents: "none",
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, calc(-50% + 70px))",
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${C.teal} 0%, #1f7a78 100%)`,
                    border: "2px solid rgba(255,255,255,0.95)",
                    boxShadow: "0 6px 24px rgba(0,0,0,0.6), 0 0 0 6px rgba(56,178,172,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      width: 0,
                      height: 0,
                      marginLeft: 5,
                      borderLeft: "16px solid #fff",
                      borderTop: "10px solid transparent",
                      borderBottom: "10px solid transparent",
                    }}
                  />
                </span>
                <span
                  style={{
                    position: "absolute",
                    left: 12,
                    right: 12,
                    bottom: 14,
                    textAlign: "center",
                    fontSize: 13,
                    color: "#fff",
                    textShadow: "0 1px 4px rgba(0,0,0,0.85)",
                    fontWeight: 600,
                  }}
                >
                  Watch the intro
                  <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.85, marginTop: 2 }}>
                    Tap to play with sound
                  </div>
                </span>
              </button>
            )}

            {/* Expand / collapse overlay — same styling as section toggles elsewhere in the app */}
            {introVideoPlaying && (
              <>
              <button
                onClick={() => setIntroVideoExpanded((v) => !v)}
                type="button"
                title={introVideoExpanded ? "Collapse video" : "Expand video"}
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  background: "rgba(0,0,0,0.55)",
                  border: "none",
                  color: C.muted,
                  cursor: "pointer",
                  fontSize: 10,
                  padding: "4px 8px",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  zIndex: 2,
                  transform: "translateZ(0)",
                  WebkitTransform: "translateZ(0)",
                }}
              >
                <span>{introVideoExpanded ? "↓" : "↑"}</span>
                <span>{introVideoExpanded ? "collapse" : "expand"}</span>
              </button>

              {/* Pause / Resume + Stop — bottom-left, app's standard toggle styling */}
              <div
                style={{
                  position: "absolute",
                  bottom: 6,
                  left: 6,
                  display: "flex",
                  gap: 6,
                  zIndex: 2,
                  transform: "translateZ(0)",
                  WebkitTransform: "translateZ(0)",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    const v = introVideoRef.current;
                    if (!v) return;
                    if (v.paused) v.play(); else v.pause();
                  }}
                  title={introVideoPaused ? "Resume" : "Pause"}
                  style={{
                    background: "rgba(0,0,0,0.55)",
                    border: "none",
                    color: C.muted,
                    cursor: "pointer",
                    fontSize: 10,
                    padding: "4px 8px",
                    borderRadius: 6,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span>{introVideoPaused ? "▶" : "❚❚"}</span>
                  <span>{introVideoPaused ? "play" : "pause"}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const v = introVideoRef.current;
                    if (v) {
                      try { v.pause(); v.currentTime = 0; } catch {}
                    }
                    setIntroVideoPlaying(false);
                    setIntroVideoExpanded(false);
                    setIntroVideoPaused(false);
                  }}
                  title="Stop"
                  style={{
                    background: "rgba(0,0,0,0.55)",
                    border: "none",
                    color: C.muted,
                    cursor: "pointer",
                    fontSize: 10,
                    padding: "4px 8px",
                    borderRadius: 6,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span>■</span>
                  <span>stop</span>
                </button>
              </div>
              </>
            )}
          </div>
        </div>
        )}

        {/* Frosted glass card for all controls */}
        <div
          style={{
            background: "rgba(4,8,15,0.65)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: 16,
            border: `1px solid ${C.border}`,
            padding: "16px 14px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
        <button
          type="button"
          id="auth-provider-google"
          data-testid="auth-provider-google"
          onClick={() => handleProviderSignIn(googleProvider)}
          disabled={loading}
          style={{
            width: "100%",
            padding: "11px 12px",
            borderRadius: 10,
            border: `1px solid ${C.seafoam}66`,
            background: "rgba(46,191,165,0.12)",
            color: authSurfaceText,
            cursor: loading ? "default" : "pointer",
            fontWeight: 700,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </span>
        </button>

        <button
          type="button"
          id="auth-provider-facebook"
          data-testid="auth-provider-facebook"
          onClick={() => handleProviderSignIn(facebookProvider)}
          disabled={loading}
          style={{
            width: "100%",
            padding: "11px 12px",
            borderRadius: 10,
            border: "1px solid #2e5c9a",
            background: "rgba(46,92,154,0.18)",
            color: authSurfaceText,
            cursor: loading ? "default" : "pointer",
            fontWeight: 700,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="#1877F2">
              <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
            </svg>
            {oauthFlowStage === "redirecting" && oauthFlowProvider === "facebook.com"
              ? "Opening Facebook..."
              : oauthFlowStage === "returning" && oauthFlowProvider === "facebook.com"
              ? "Finishing Facebook Sign-In..."
              : "Continue with Facebook"}
          </span>
        </button>

        {mode === "signup" && (
          <div style={{ fontSize: 10, color: authSurfaceMuted, lineHeight: 1.4, textAlign: "center" }}>
            By continuing with Google or Facebook you agree to the EULA.
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "4px 0" }}>
          <div style={{ flex: 1, height: 1, background: "#1e3448" }} />
          <div style={{ fontSize: 10, color: authSurfaceMuted, letterSpacing: 1 }}>OR EMAIL</div>
          <div style={{ flex: 1, height: 1, background: "#1e3448" }} />
        </div>

        {signupSwitchBanner && (
          <div
            role="status"
            style={{
              border: "1px solid rgba(240,186,73,0.65)",
              background: "rgba(240,186,73,0.16)",
              borderRadius: 10,
              padding: "9px 10px",
              color: "#FFE6A8",
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.4,
            }}
          >
            {signupSwitchBanner}
          </div>
        )}

        <form onSubmit={handleEmailSubmit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {mode === "signup" && (
            <div style={{ display: "flex", gap: 10 }}>
              <input
                type="text"
                id="auth-signup-firstname"
                data-testid="auth-signup-firstname"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                autoComplete="given-name"
                style={{
                  width: "50%",
                  boxSizing: "border-box",
                  padding: "11px 12px",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: C.cardBg,
                  color: C.white,
                  outline: "none",
                }}
              />
              <input
                type="text"
                id="auth-signup-lastname"
                data-testid="auth-signup-lastname"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                autoComplete="family-name"
                style={{
                  width: "50%",
                  boxSizing: "border-box",
                  padding: "11px 12px",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: C.cardBg,
                  color: C.white,
                  outline: "none",
                }}
              />
            </div>
          )}
          {mode === "signup" && (
            <input
              type="tel"
              id="auth-signup-phone"
              data-testid="auth-signup-phone"
              value={phone}
              onChange={(e) => setPhone(formatPhoneForDisplay(e.target.value))}
              placeholder="(___) ___-____"
              autoComplete="tel"
              inputMode="tel"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "11px 12px",
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                background: C.cardBg,
                color: C.white,
                outline: "none",
              }}
            />
          )}
          <input
            type="email"
            id="auth-signup-email"
            data-testid="auth-signup-email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (signupSwitchBanner) setSignupSwitchBanner("");
            }}
            placeholder="Email"
            autoComplete="email"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "11px 12px",
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: C.cardBg,
              color: C.white,
              outline: "none",
            }}
          />
          <div style={{ position: "relative" }}>
            <input
              type={showPassword ? "text" : "password"}
              id="auth-signup-password"
              data-testid="auth-signup-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "signup" ? "Password (optional — set on activation)" : "Password"}
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "11px 40px 11px 12px",
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                background: C.cardBg,
                color: C.white,
                outline: "none",
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: C.muted,
                cursor: "pointer",
                padding: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              {showPassword ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>
          {mode === "signin" && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -4 }}>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={loading}
                style={{
                  background: "none",
                  border: "none",
                  color: C.seafoam,
                  fontSize: 11,
                  cursor: loading ? "default" : "pointer",
                  padding: "2px 0",
                  textDecoration: "underline",
                }}
              >
                Forgot password?
              </button>
            </div>
          )}
          {mode === "signup" && (
            <div
              style={{
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                background: "rgba(6,10,16,0.55)",
                padding: "10px 10px 8px",
                color: C.muted,
              }}
            >
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 10, color: C.white, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  id="auth-signup-eula"
                  data-testid="auth-signup-eula"
                  checked={eulaAccepted}
                  onChange={(e) => setEulaAccepted(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  I agree to the End User License Agreement (Version {EULA_VERSION}).
                </span>
              </label>
              <div style={{ marginTop: 8, fontSize: 10, lineHeight: 1.4 }}>
                Plain-language summary: THE 99 helps you log catches and tournament activity. You are responsible for safe use, legal compliance, and account security. AI insights are informational only. By creating an account, you accept these terms.
              </div>
              <div style={{ marginTop: 8, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button type="button" onClick={() => setLegalView("eula")} style={{ background: "none", border: "none", color: C.seafoam, fontSize: 10, cursor: "pointer", padding: 0 }}>View EULA</button>
                <button type="button" onClick={() => setLegalView("privacy")} style={{ background: "none", border: "none", color: C.seafoam, fontSize: 10, cursor: "pointer", padding: 0 }}>Privacy Policy</button>
              </div>
            </div>
          )}
          {mode === "signup" && (
            <div
              {...(hasAutoBookAttribution ? { "data-testid": "auth-book-attribution-banner" } : {})}
              style={{
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                background: "rgba(6,10,16,0.55)",
                padding: "10px 10px 8px",
                color: C.muted,
                opacity: hasAutoBookAttribution ? 1 : 0.7,
              }}
            >
              <label
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                  fontSize: 10,
                  color: C.white,
                  cursor: "default",
                }}
              >
                <input
                  type="checkbox"
                  checked={hasAutoBookAttribution}
                  disabled
                  readOnly
                  aria-describedby="book-signup-help"
                  style={{ marginTop: 2 }}
                />
                <span>
                  I'm signing up because I bought <em>Before the Cast</em> (or scanned its QR code).
                </span>
              </label>
              <div id="book-signup-help" style={{ marginTop: 6, fontSize: 10, lineHeight: 1.4 }}>
                {hasAutoBookAttribution
                  ? "Verified — we'll send a thank-you with your activation email so we can keep building features for readers like you."
                  : "This option is enabled automatically when you sign up by scanning the QR code inside Before the Cast. It cannot be selected manually."}
              </div>
            </div>
          )}
          <Button
            type="submit"
            variant="primary"
            fullWidth
            id="auth-signup-submit"
            data-testid="auth-signup-submit"
            disabled={loading}
          >
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M20 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" fill={C.dark}/>
              </svg>
              {loading ? "Working..." : modeLabel}
            </span>
          </Button>
        </form>

        <button
          type="button"
          id={mode === "signin" ? "auth-mode-signup" : "auth-mode-signin"}
          data-testid={mode === "signin" ? "auth-mode-signup" : "auth-mode-signin"}
          onClick={() => {
            setError("");
            setSignupSwitchBanner("");
            if (mode === "signup") {
              setFirstName("");
              setLastName("");
              setEulaAccepted(false);
            }
            setMode((m) => (m === "signin" ? "signup" : "signin"));
          }}
          style={{
            marginTop: 2,
            background: "transparent",
            border: "none",
            color: C.seafoam,
            cursor: "pointer",
            fontSize: 11,
          }}
        >
          {mode === "signin" ? "Need an account? Create one" : "Cancel — Already have an account? Sign in"}
        </button>

        {error && (
          <div style={{ fontSize: 11, color: C.warning, marginTop: 4, lineHeight: 1.4 }}>
            {error}
          </div>
        )}
        </div>{/* end frosted card */}

        <div style={{ fontSize: 10, color: authSurfaceMuted, textAlign: "center", marginTop: 4 }}>
          Version: {APP_VERSION}
        </div>

        {/* Rotating sponsor band (Option A) — only renders when the dashboard
            has published active login sponsors, so regular users see no gap. */}
        {loginSponsors.length > 0 && (
          <SponsorBand sponsors={loginSponsors} mutedColor={authSurfaceMuted} />
        )}

        {/* "Get the book" — shown to visitors who did NOT arrive via the book's
            QR code (i.e. likely don't own it yet). Links out to the publisher. */}
        {!hasAutoBookAttribution && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <a
              href="https://oneelevenpub.com/"
              target="_blank"
              rel="noopener noreferrer"
              id="auth-get-book"
              data-testid="auth-get-book"
              aria-label="Get the book — Before the Cast"
              title="Get the book — Before the Cast"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 16px",
                borderRadius: 999,
                border: `1px solid ${C.seafoam}66`,
                background: "rgba(6,10,16,0.55)",
                color: authSurfaceText,
                textDecoration: "none",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
              }}
            >
              <span aria-hidden style={{ display: "inline-flex", lineHeight: 0, color: C.seafoam }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </span>
              Get the book
            </a>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            marginTop: 10,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 16px",
              borderRadius: 999,
              border: "none",
              background: "transparent",
              color: authSurfaceMuted,
              fontSize: 11,
              letterSpacing: 1.4,
              textTransform: "uppercase",
            }}
            title="GoPro HERO compatible"
          >
            <span style={{ fontWeight: 600 }}>Compatible with</span>
            <img
              src="/assets/gopro-logo-transparent%202.png"
              alt="GoPro"
              style={{ height: 40, width: "auto", display: "block" }}
            />
          </span>
        </div>

        <div style={{ position: "absolute", right: 24, bottom: 24, zIndex: 40 }}>
          {showLegalLinks && (
            <div
              style={{
                marginBottom: 8,
                width: 200,
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                background: "rgba(6,10,16,0.94)",
                padding: "8px 10px",
                boxShadow: "0 10px 24px rgba(0,0,0,0.45)",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <button
                type="button"
                onClick={() => { setShowLegalLinks(false); setLegalView("privacy"); }}
                style={{ color: C.muted, fontSize: 10, textDecoration: "none", background: "none", border: "none", cursor: "pointer" }}
              >Privacy Policy</button>
              <button
                type="button"
                onClick={() => { setShowLegalLinks(false); setLegalView("deletion"); }}
                style={{ color: C.muted, fontSize: 10, textDecoration: "none", background: "none", border: "none", cursor: "pointer" }}
              >Data Deletion</button>
              <button
                type="button"
                onClick={() => { setShowLegalLinks(false); setLegalView("eula"); }}
                style={{ color: C.muted, fontSize: 10, textDecoration: "none", background: "none", border: "none", cursor: "pointer" }}
              >EULA</button>
            </div>
          )}
          {!showLegalLinks && (
            <button
              type="button"
              onClick={() => setShowLegalLinks(true)}
              aria-label="Open legal links"
              style={{
                width: 24, height: 24, borderRadius: "50%",
                border: `1px solid ${C.border}`,
                background: "rgba(6,10,16,0.65)",
                color: C.muted, cursor: "pointer", fontSize: 12,
                lineHeight: 1, display: "grid", placeItems: "center", padding: 0,
              }}
            >i</button>
          )}
        </div>

        </div>{/* end scrollable content layer */}

        {oauthFlowStage && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(3,8,14,0.82)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              display: "grid",
              placeItems: "center",
              zIndex: 70,
              padding: "18px",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 320,
                borderRadius: 14,
                border: `1px solid ${oauthProviderTheme.panelBorder}`,
                background: "rgba(8,14,24,0.94)",
                boxShadow: oauthProviderTheme.panelGlow,
                padding: "16px 14px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 11, letterSpacing: 2, color: oauthProviderTheme.accent, fontWeight: 700 }}>
                THE 99 SECURE LOGIN
              </div>
              <div style={{ marginTop: 10, display: "grid", placeItems: "center" }}>
                <div
                  style={{
                    position: "relative",
                    width: 44,
                    height: 44,
                    borderRadius: 999,
                    background: oauthProviderTheme.iconBg,
                    display: "grid",
                    placeItems: "center",
                    animation: "oauthIconFloat 1.4s ease-in-out infinite",
                    boxShadow: `0 6px 20px ${oauthProviderTheme.accentSoft}`,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: -8,
                      borderRadius: 999,
                      border: `2px solid ${oauthProviderTheme.accentSoft}`,
                      animation: "oauthPulseRing 1.6s ease-out infinite",
                    }}
                  />
                  {oauthFlowProvider === "facebook.com" ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="#FFFFFF">
                      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 2l7 4v6c0 5.25-3.57 9.86-7 11-3.43-1.14-7-5.75-7-11V6l7-4z" fill="#FFFFFF" opacity="0.95"/>
                    </svg>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 18, color: authSurfaceText, fontWeight: 800, marginTop: 8 }}>
                {oauthFlowStage === "redirecting"
                  ? `Opening ${getProviderLabel(oauthFlowProvider)} Sign-In`
                  : oauthFlowStage === "opening"
                  ? `Connecting to ${getProviderLabel(oauthFlowProvider)}`
                  : `Finishing ${getProviderLabel(oauthFlowProvider)} Sign-In`}
              </div>
              <div style={{ fontSize: 12, color: authSurfaceMuted, lineHeight: 1.45, marginTop: 8 }}>
                {oauthFlowStage === "redirecting"
                  ? "Your browser is switching to a secure provider page. You will return automatically."
                  : oauthFlowStage === "opening"
                  ? "A secure sign-in window has opened. Complete sign-in there to continue."
                  : "We are verifying your account and will take you to the app in a moment."}
              </div>
              <div style={{ marginTop: 12, display: "flex", justifyContent: "center", alignItems: "center", gap: 8, color: oauthProviderTheme.accent, fontSize: 12, fontWeight: 700 }}>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 999, background: oauthProviderTheme.accent, boxShadow: `0 0 0 6px ${oauthProviderTheme.accentSoft}` }} />
                Processing Secure OAuth
              </div>
              <Button
                variant="ghost"
                onClick={async () => {
                  if (isCapacitorNative()) {
                    try {
                      const { FirebaseAuthentication } = await import(
                        "@capacitor-firebase/authentication"
                      );
                      await FirebaseAuthentication.signOut();
                    } catch {}
                  }
                  setOauthFlowStage("");
                  setOauthFlowProvider("");
                  setLoading(false);
                }}
                style={{
                  marginTop: 14,
                  border: `1px solid ${oauthProviderTheme.accentSoft}`,
                }}
              >
                CANCEL
              </Button>
            </div>
          </div>
        )}

      </div>
  );
}

